import { useCallback, useEffect, useRef, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

// ----------------------------------------------------------------------

const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="combobox"]',
  'input',
  'textarea',
  'select',
  '.MuiChip-deleteIcon',
].join(',');

const ICON_HINTS = {
  add: 'Add',
  plus: 'Add',
  create: 'Create',
  edit: 'Edit',
  pencil: 'Edit',
  delete: 'Delete',
  trash: 'Delete',
  remove: 'Remove',
  close: 'Close',
  search: 'Search',
  filter: 'Filter',
  refresh: 'Refresh',
  reload: 'Refresh',
  download: 'Download',
  upload: 'Upload',
  print: 'Print',
  view: 'View',
  eye: 'View',
  copy: 'Copy',
  share: 'Share',
  send: 'Send',
  save: 'Save',
  calendar: 'Calendar',
  date: 'Date',
  chevron: 'Expand or collapse',
  arrow: 'Navigate',
  back: 'Back',
  forward: 'Forward',
  menu: 'Menu',
  more: 'More actions',
  settings: 'Settings',
  notification: 'Notifications',
  attach: 'Attach file',
  gallery: 'Add image',
  image: 'Image',
  file: 'File',
  document: 'Document',
  reply: 'Reply',
  microphone: 'Voice input',
  user: 'User',
  group: 'Group',
};

function cleanText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getVisibleText(element) {
  const clone = element.cloneNode(true);
  clone.querySelectorAll('svg, .component-iconify, .MuiTouchRipple-root').forEach((node) => node.remove());
  return cleanText(clone.textContent);
}

function labelFromIconName(iconName) {
  const normalized = cleanText(iconName)
    .replace(/^[^:]+:/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b(fill|bold|outline|linear|duotone|rounded|line|solid)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  if (!normalized) return '';

  const matchingKey = Object.keys(ICON_HINTS).find((key) => normalized.includes(key));
  if (matchingKey) return ICON_HINTS[matchingKey];

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getElementHint(element) {
  const explicitHint =
    element.getAttribute('title') ||
    element.getAttribute('data-tooltip') ||
    element.getAttribute('data-help');

  if (explicitHint) return cleanText(explicitHint);

  const visibleText = getVisibleText(element);
  if (visibleText) return '';

  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return cleanText(ariaLabel);

  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) {
    return '';
  }

  const icon = element.querySelector('.component-iconify');
  const iconName = icon?.getAttribute('data-icon');
  if (iconName) return labelFromIconName(iconName);

  return '';
}

function clearAutoHint(element) {
  if (element.dataset.autoHintTitle === 'true' && element.dataset.autoHintOriginalTitle) {
    element.setAttribute('title', element.dataset.autoHintOriginalTitle);
  }
  if (element.dataset.autoHintAria === 'true') {
    element.removeAttribute('aria-label');
  }
  delete element.dataset.autoHintText;
  delete element.dataset.autoHintTitle;
  delete element.dataset.autoHintOriginalTitle;
  delete element.dataset.autoHintAria;
}

function applyHint(element) {
  if (!(element instanceof HTMLElement)) return;
  if (element.dataset.autoHint === 'skip') return;
  if (element.getAttribute('data-mui-internal-clone-element') === 'true') return;

  const hint = getElementHint(element);
  if (!hint) {
    clearAutoHint(element);
    return;
  }

  element.dataset.autoHintText = hint;

  if (element.getAttribute('title')) {
    element.dataset.autoHintOriginalTitle = element.getAttribute('title');
    element.removeAttribute('title');
  }

  const needsAria =
    element.tagName === 'BUTTON' ||
    element.tagName === 'A' ||
    element.getAttribute('role') === 'button' ||
    element.getAttribute('role') === 'menuitem' ||
    element.getAttribute('role') === 'tab';

  if (needsAria && !element.getAttribute('aria-label')) {
    element.setAttribute('aria-label', hint);
    element.dataset.autoHintAria = 'true';
  }
}

function applyHints(root = document) {
  root.querySelectorAll(INTERACTIVE_SELECTOR).forEach(applyHint);
}

export default function UiHintsProvider() {
  const hideTimerRef = useRef(null);
  const [tooltip, setTooltip] = useState({ open: false, title: '', rect: null });

  const closeTooltip = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      setTooltip((prev) => ({ ...prev, open: false }));
    }, 80);
  }, []);

  const openTooltip = useCallback((target) => {
    if (!(target instanceof HTMLElement)) return;
    const hintTarget = target.closest?.('[data-auto-hint-text]');
    if (!(hintTarget instanceof HTMLElement)) return;

    const title = hintTarget.dataset.autoHintText;
    if (!title) return;

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    setTooltip({
      open: true,
      title,
      rect: hintTarget.getBoundingClientRect(),
    });
  }, []);

  useEffect(() => {
    applyHints();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches?.(INTERACTIVE_SELECTOR)) {
            applyHint(node);
          }
          applyHints(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleMouseOver = (event) => openTooltip(event.target);
    const handleFocusIn = (event) => openTooltip(event.target);
    const handleMouseOut = (event) => {
      const currentTarget = event.target?.closest?.('[data-auto-hint-text]');
      const nextTarget = event.relatedTarget?.closest?.('[data-auto-hint-text]');
      if (currentTarget && currentTarget === nextTarget) return;
      closeTooltip();
    };
    const handleFocusOut = () => closeTooltip();
    const handleScroll = () => closeTooltip();

    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('focusout', handleFocusOut, true);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      observer.disconnect();
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [closeTooltip, openTooltip]);

  const rect = tooltip.rect;

  return (
    <Tooltip title={tooltip.title} open={tooltip.open && !!rect} arrow placement="top">
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          pointerEvents: 'none',
          top: rect?.top ?? 0,
          left: rect?.left ?? 0,
          width: rect?.width ?? 0,
          height: rect?.height ?? 0,
          zIndex: -1,
        }}
      />
    </Tooltip>
  );
}
